import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function AdminDisputes() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("under_review");
  const [notes, setNotes] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    const params = filter === "all" ? {} : { status: filter };
    api.get("/admin/disputes", { params })
      .then(({ data }) => setItems(data))
      .catch((err) => console.warn("disputes load failed", err))
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (did, resolution) => {
    try {
      await api.post(`/admin/disputes/${did}/resolve`, { resolution, notes: notes[did] || null });
      toast.success("Dispute resolved");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-disputes">
      <div className="flex gap-2 flex-wrap">
        {["under_review", "resolved", "all"].map((s) => (
          <button
            key={s}
            data-testid={`disp-filter-${s}`}
            onClick={() => setFilter(s)}
            className={`px-3 py-1 border-2 border-black font-mono-b text-xs font-bold uppercase ${filter === s ? "bg-black text-[#D4FF00]" : "bg-white"}`}
          >
            {s.replace("_", " ")}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="font-mono-b p-6" data-testid="disp-loading">Loading...</p>
      ) : items.length === 0 ? (
        <p className="font-mono-b p-8 text-center border-2 border-dashed border-black">No disputes here.</p>
      ) : (
        <div className="space-y-3">
          {items.map((d) => (
            <div key={d.dispute_id} className="bg-white border-2 border-black brutal-shadow-sm p-4" data-testid={`disp-card-${d.dispute_id}`}>
              <div className="flex justify-between flex-wrap gap-2">
                <div>
                  <p className="font-mono-b text-xs text-black/60">{d.dispute_id}</p>
                  <p className="font-display text-lg">Order {d.order_id?.slice(0, 16)} · {d.buyer_email}</p>
                  <p className="font-mono-b text-xs mt-1"><b>{d.reason?.replace("_", " ").toUpperCase()}</b></p>
                  <p className="font-mono-b text-xs mt-1 text-black/70">{d.description}</p>
                </div>
                <span className={`sticker text-[10px] ${d.status === "resolved" ? "" : "pink"}`}>
                  {d.status?.replace("_", " ").toUpperCase()}
                </span>
              </div>
              {d.evidence_urls?.length > 0 && (
                <div className="mt-3 grid grid-cols-4 gap-2">
                  {d.evidence_urls.map((u, i) => (
                    <a key={i} href={u} target="_blank" rel="noreferrer" className="block border-2 border-black">
                      <img src={u} alt="evidence" className="w-full aspect-square object-cover" />
                    </a>
                  ))}
                </div>
              )}
              {d.status === "under_review" && (
                <>
                  <input
                    placeholder="Notes to buyer (optional)"
                    value={notes[d.dispute_id] || ""}
                    onChange={(e) => setNotes({ ...notes, [d.dispute_id]: e.target.value })}
                    className="brutal-input w-full mt-3 text-xs"
                    data-testid={`disp-notes-${d.dispute_id}`}
                  />
                  <div className="mt-2 flex gap-2 flex-wrap">
                    <button
                      onClick={() => resolve(d.dispute_id, "refund_buyer")}
                      data-testid={`disp-refund-${d.dispute_id}`}
                      className="bg-[#D4FF00] border-2 border-black px-3 py-2 font-mono-b font-bold uppercase text-xs hover-nudge"
                    >
                      REFUND BUYER
                    </button>
                    <button
                      onClick={() => resolve(d.dispute_id, "release_to_seller")}
                      data-testid={`disp-release-${d.dispute_id}`}
                      className="bg-white border-2 border-black px-3 py-2 font-mono-b font-bold uppercase text-xs hover-nudge"
                    >
                      RELEASE TO SELLER
                    </button>
                    <button
                      onClick={() => resolve(d.dispute_id, "dismiss")}
                      data-testid={`disp-dismiss-${d.dispute_id}`}
                      className="bg-[#F4F4F0] border-2 border-black px-3 py-2 font-mono-b font-bold uppercase text-xs hover-nudge"
                    >
                      DISMISS
                    </button>
                  </div>
                </>
              )}
              {d.notes && d.status === "resolved" && (
                <p className="mt-2 font-mono-b text-[11px] bg-[#F4F4F0] border-2 border-black p-2">
                  <b>Resolution:</b> {d.resolution?.replace("_", " ")} — {d.notes}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
